import React, {useState} from 'react';
import {StyleSheet} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {savePersonnel} from '@/entities/personnel/api';
import type {
  AttendanceSchedule,
  PersonnelCandidate,
  PersonnelInput,
  PersonnelRecord,
} from '@/entities/personnel/types';
import {isoDate, parseIsoDate} from '@/entities/attendance/period';
import {fieldConflict, messageOf} from '@/shared/lib/errors';
import {nextEmployeeNumber} from '@/shared/lib/employee-number';
import {rosterQuery} from '@/features/manage-personnel/lib/roster';
import {FaceCaptureField} from '@/features/capture-face/ui/FaceCaptureField';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {DateField} from '@/shared/ui/date-field';
import {FormCard, FormLayout, FormRow} from '@/shared/ui/form';
import {AppInput} from '@/shared/ui/input';
import {Toggle} from '@/shared/ui/toggle';
import {spacing} from '@/shared/ui/theme';
import {CandidateNotice} from './CandidateNotice';
import {DepartmentPicker} from './DepartmentPicker';
import {ScheduleEditor} from './ScheduleEditor';

/**
 * Create or edit a person. On edit the caller passes the FULL record (avatar hydrated),
 * and this form round-trips the terminal-owned fields — face_template, threshold,
 * sample_count, created_at_millis — unchanged. Sending them empty would let a metadata
 * edit wipe an enrolled face, which the terminal, not the phone, owns.
 * `account_id` is never in the payload, so editing cannot break an employee's link.
 *
 * ## Why the access deadline is a switch and a date, not a number
 *
 * `access_until_millis` is the ONLY access lever the model has — there is no
 * "enabled" flag on a person at all (`sv_gateway/src/openapi/personnel.rs:119-124`,
 * quoted in docs/api/conventions.md §8). And `0` there does not mean "expired now",
 * it means NO LIMIT. A bare number field would let an operator type a plausible `0`
 * and grant permanent access while believing they revoked it, so the two states are
 * separated: the toggle decides limited/unlimited, the picker only exists inside the
 * limited branch, and the value written is the END of the chosen day — a pass valid
 * "until the 20th" that stopped working at midnight on the 20th would be read as a
 * bug by everyone except the person who wrote it.
 */
const DAY_END_MS = 24 * 60 * 60 * 1000 - 1;

const styles = StyleSheet.create({
  root: {paddingBottom: spacing(8)},
});

export function PersonnelForm({
  companyId,
  record,
  onSaved,
  onOpenConflict,
}: {
  companyId: string;
  /** Full record for edit; absent ⇒ create. */
  record?: PersonnelRecord;
  onSaved: () => void;
  /**
   * Open the card that already holds the employee number this save collided with.
   * Absent ⇒ the conflict is reported without the shortcut, which is what a caller
   * with no navigator (a test, a future embed) gets.
   */
  onOpenConflict?: (recordId: string) => void;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const [fullName, setFullName] = useState(record?.full_name ?? '');
  const [position, setPosition] = useState(record?.position ?? '');
  const [employeeNumber, setEmployeeNumber] = useState(record?.employee_number ?? '');
  const [birthDate, setBirthDate] = useState(record?.birth_date ?? '');
  const [departmentIds, setDepartmentIds] = useState<string[]>(
    record?.department_ids ?? [],
  );
  const [schedule, setSchedule] = useState<AttendanceSchedule | null>(
    record?.attendance_schedule ?? null,
  );
  const [nfcUid, setNfcUid] = useState(record?.nfc_uid ?? '');
  // 0 on the wire means "no limit", so the switch reads the number rather than a
  // separate flag the server does not have.
  const [accessLimited, setAccessLimited] = useState(
    (record?.access_until_millis ?? 0) > 0,
  );
  const [accessUntil, setAccessUntil] = useState(() =>
    isoDate(
      record?.access_until_millis
        ? new Date(record.access_until_millis)
        : new Date(),
    ),
  );
  // Seeded from the hydrated record so an untouched edit round-trips the enrolled photo;
  // FaceCaptureField only changes this when the user captures/picks/removes, so a
  // metadata edit never wipes the face (empty is sent ONLY on an explicit removal).
  const [avatarBase64, setAvatarBase64] = useState(record?.avatar_base64 ?? '');
  /**
   * A detached record this create is putting BACK, rather than a new person.
   *
   * Only meaningful while creating: on an edit the record id is already settled. Never
   * seeded from anything — the choice is always the operator's, see `CandidateNotice`.
   */
  const [reusing, setReusing] = useState<PersonnelCandidate | null>(null);

  /**
   * «Сгенерировать» — the next free number, worked out from the roster.
   *
   * The roster is fetched ON PRESS, not with the form: five hundred people is a
   * heavy thing to load for a field most operators fill by hand, and the button
   * is the only thing that needs it. It goes through `fetchQuery` under the bulk
   * sheet's own key, so a roster either sheet has already loaded is reused and a
   * second press costs nothing.
   *
   * The suggestion can still collide — the roster stops at `ROSTER_LIMIT`, and
   * another operator may be filling the same form right now. That is what the
   * 409 below already handles, and it is why this stays a suggestion in an
   * editable field rather than a number the form claims is reserved.
   */
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  /**
   * Offered while the number is EMPTY on the server, which is every create and
   * the edit of somebody who was never given one. Read off `record` rather than
   * off the live field, so the button cannot appear and vanish under the thumb
   * as the operator types — and so it can never overwrite a number that already
   * identifies a person in an export somebody has already filed.
   */
  const canGenerate = !record?.employee_number?.trim();

  async function generateEmployeeNumber() {
    setGenerating(true);
    setGenerateError('');
    try {
      const roster = await qc.fetchQuery({
        ...rosterQuery(companyId),
        // A minute is long enough for "add three people in a row" to reuse one
        // fetch, short enough that a colleague's new person is not missed twice.
        staleTime: 60_000,
      });
      setEmployeeNumber(
        nextEmployeeNumber((roster.data ?? []).map(r => r.employee_number)),
      );
    } catch (e) {
      setGenerateError(messageOf(e, t('errors.unknown')));
    } finally {
      setGenerating(false);
    }
  }

  const save = useMutation({
    mutationFn: () => {
      const input: PersonnelInput = {
        // Saving under a detached person's id is what re-attaches them, history and
        // all — the API has no merge call and will not get one. Empty ⇒ a new row.
        record_id: record?.record_id ?? reusing?.record_id ?? '',
        full_name: fullName.trim(),
        birth_date: birthDate.trim(),
        employee_number: employeeNumber.trim(),
        position: position.trim(),
        access_until_millis: accessLimited
          ? parseIsoDate(accessUntil).getTime() + DAY_END_MS
          : 0,
        // Terminal-owned — round-trip unchanged, default empty on create.
        face_template: record?.face_template ?? '',
        threshold: record?.threshold ?? 0,
        sample_count: record?.sample_count ?? 0,
        created_at_millis: record?.created_at_millis ?? 0,
        // Face image the terminal enrols from — captured/picked here or round-tripped.
        avatar_base64: avatarBase64,
        department_ids: departmentIds,
        attendance_schedule: schedule,
        // Empty string is not a card: the field is optional on the wire, and sending
        // "" would claim the person carries a card whose uid is blank.
        nfc_uid: nfcUid.trim() || null,
        notify_telegram_ids: record?.notify_telegram_ids ?? [],
      };
      return savePersonnel(companyId, input);
    },
    onSuccess: async saved => {
      await Promise.all([
        qc.invalidateQueries({queryKey: ['personnel-list', companyId]}),
        qc.invalidateQueries({queryKey: ['personnel', companyId, saved.record_id]}),
        qc.invalidateQueries({
          queryKey: ['personnel-attendance', companyId, saved.record_id],
        }),
      ]);
      onSaved();
    },
  });

  // sv_control's save_personnel rejects an empty department set ("Choose at least one
  // department"), so require one client-side — a Russian hint beats surfacing the raw
  // server error. Note: which departments actually PERSIST is a separate backend matter
  // (control.departments is a projection populated by the terminal/desktop sync), but the
  // request must carry at least one id regardless.
  const nameValid = fullName.trim().length > 0;
  const deptValid = departmentIds.length > 0;
  const canSave = nameValid && deptValid && !save.isPending;

  // A 409 naming the card that already holds this employee number belongs AT the
  // number field, not in a banner at the bottom of a form the operator has scrolled
  // past. Anything else — a network failure, an ambiguous id, a 403 — still falls
  // through to the banner, because we cannot say which field it is about.
  const numberTaken = fieldConflict(save.error, 'record');

  /*
    Раскладка — shadcn «edit record»: главное слева, второстепенное (фото, срок)
    колонкой справа; короткие поля парами. На телефоне то же самое столбиком. См.
    `shared/ui/form`.
  */
  const main = (
    <>
      {/* Заголовка здесь нет: `Page` печатает ровно эту же строку над формой. Форма —
          не экран и своё имя не объявляет. */}
      <FormCard title={t('personnel.sectionMain')} hint={t('personnel.sectionMainHint')}>
        <AppInput
          label={t('personnel.fullName')}
          value={fullName}
          onChangeText={setFullName}
          placeholder={t('personnel.fullNamePlaceholder')}
          icon="user"
        />
        <FormRow>
          <AppInput
            label={t('personnel.position')}
            value={position}
            onChangeText={setPosition}
            placeholder={t('personnel.positionPlaceholder')}
          />
          <AppInput
            label={t('personnel.employeeNumber')}
            value={employeeNumber}
            onChangeText={setEmployeeNumber}
            placeholder={t('personnel.employeeNumberPlaceholder')}
            error={
              numberTaken
                ? t('personnel.numberTaken', {number: numberTaken.value})
                : undefined
            }
            hint={t('personnel.employeeNumberHint')}
            trailing={
              canGenerate ? (
                <AppButton
                  title={t('personnel.employeeNumberGenerate')}
                  tone="outline"
                  size="xs"
                  icon="refresh"
                  loading={generating}
                  onPress={() => void generateEmployeeNumber()}
                  testID="personnel-employee-number-generate"
                />
              ) : undefined
            }
            testID="personnel-employee-number"
          />
        </FormRow>
        {numberTaken && onOpenConflict ? (
          <AppButton
            title={t('personnel.numberTakenOpen')}
            tone="ghost"
            size="sm"
            icon="link"
            onPress={() => onOpenConflict(numberTaken.ownerId)}
            testID="personnel-open-conflict"
          />
        ) : null}
        <FormRow>
          <AppInput
            label={t('personnel.birthDate')}
            value={birthDate}
            onChangeText={setBirthDate}
            placeholder="1990-01-31"
            keyboardType="numbers-and-punctuation"
          />
          <AppInput
            label={t('personnel.nfcUid')}
            value={nfcUid}
            onChangeText={setNfcUid}
            placeholder={t('personnel.nfcUidPlaceholder')}
            hint={t('personnel.nfcUidHint')}
            autoCapitalize="characters"
            testID="personnel-nfc-uid"
          />
        </FormRow>
      </FormCard>

      {/* Asked before the face is captured and before departments are picked: the
          answer can change which record this save writes to, and finding that out
          after the operator has photographed somebody is finding out too late. */}
      {record ? null : (
        <CandidateNotice
          companyId={companyId}
          fullName={fullName}
          birthDate={birthDate}
          employeeNumber={employeeNumber}
          chosen={reusing}
          onChoose={setReusing}
          onOpen={onOpenConflict}
        />
      )}

      <FormCard
        title={t('personnel.departments')}
        // The requirement is stated where the choice is made, not as an error after
        // the fact: sv_control rejects an empty set, so the form says so up front.
        hint={!deptValid ? t('personnel.departmentRequired') : undefined}>
        <DepartmentPicker
          companyId={companyId}
          value={departmentIds}
          onChange={setDepartmentIds}
        />
      </FormCard>

      <FormCard>
        <ScheduleEditor value={schedule} onChange={setSchedule} />
      </FormCard>

      {generateError ? (
        <Banner
          tone="warning"
          title={t('personnel.employeeNumberGenerateFailed')}
          message={generateError}
        />
      ) : null}

      {save.isError && !numberTaken ? (
        <Banner tone="error" title={messageOf(save.error, t('errors.unknown'))} />
      ) : null}

      <AppButton
        title={t('actions.save')}
        size="lg"
        onPress={() => save.mutate()}
        loading={save.isPending}
        disabled={!canSave}
      />
    </>
  );

  const aside = (
    <>
      <FaceCaptureField
        name={fullName || t('personnel.createTitle')}
        value={avatarBase64}
        onChange={setAvatarBase64}
      />

      {/* The only access lever the model has — see the note at the top of this file. */}
      <FormCard title={t('personnel.access')} hint={t('personnel.accessHint')}>
        <Toggle
          label={t('personnel.accessLimited')}
          hint={
            accessLimited
              ? t('personnel.accessLimitedOn')
              : t('personnel.accessLimitedOff')
          }
          value={accessLimited}
          onChange={setAccessLimited}
        />
        {accessLimited ? (
          <DateField
            label={t('personnel.accessUntil')}
            value={accessUntil}
            onChange={setAccessUntil}
            testID="personnel-access-until"
          />
        ) : null}
      </FormCard>
    </>
  );

  return <FormLayout main={main} aside={aside} style={styles.root} />;
}
